/**
 * Real operator login (replaces the old free-text Operator ID field in
 * Settings). Two steps on one screen: pick your name from the active-
 * operator list (GET /api/auth/operators — no PIN required to see this
 * list, just names), then enter your PIN. On success the session token
 * + operator identity are saved via saveOperatorSession and every
 * subsequent api.ts call sends it as X-Operator-Token.
 *
 * Reached whenever main.ts's router finds no operator session for a
 * device that otherwise has a configured Worker URL/app token, or when
 * api.ts observes a 401 and clears a stale/expired session
 * (wr:session-expired — see main.ts's listener).
 */
import { saveOperatorSession } from '../config.js';
import { api, ApiError, ApiNetworkError } from '../api.js';
import { escapeHtml, qs } from '../dom.js';
import type { OperatorSummary } from '../types.js';

export async function renderLogin(container: HTMLElement): Promise<void> {
  const div = document.createElement('div');
  div.className = 'screen';
  div.innerHTML = `
    <div class="topbar">
      <h1>Log In</h1>
    </div>
    <div id="body"><p class="hint center">Loading operators…</p></div>
  `;
  container.appendChild(div);

  const body = qs<HTMLDivElement>(div, '#body');

  let operators: OperatorSummary[];
  try {
    operators = await api.listOperators();
  } catch (err) {
    body.innerHTML = `<div class="card"><p class="error-text">${escapeHtml(describeError(err))}</p></div>`;
    return;
  }

  if (operators.length === 0) {
    body.innerHTML = `<div class="card"><p class="error-text">No active operators are set up on this server yet.</p><p class="hint">Ask a supervisor to add one in the admin site (/admin/users).</p></div>`;
    return;
  }

  renderPicker(body, operators);
}

const LAST_OPERATOR_KEY = 'wr.lastOperatorId';
/** Show the search box once the list is longer than this. */
const SEARCH_FROM = 8;

function lastOperatorId(): string | null {
  try {
    return localStorage.getItem(LAST_OPERATOR_KEY);
  } catch {
    return null;
  }
}

function rememberOperator(id: string): void {
  try {
    localStorage.setItem(LAST_OPERATOR_KEY, id);
  } catch {
    /* private mode etc. — remembering is only a convenience */
  }
}

/** Case-insensitive "contains" filter, A-Z sorted. Exported for tests. */
export function filterOperators(operators: OperatorSummary[], query: string): OperatorSummary[] {
  const q = query.trim().toLowerCase();
  return [...operators].sort((a, b) => a.name.localeCompare(b.name)).filter((o) => !q || o.name.toLowerCase().includes(q));
}

function renderPicker(body: HTMLElement, operators: OperatorSummary[]): void {
  const last = operators.find((o) => o.id === lastOperatorId());
  body.innerHTML = `
    ${last ? `<button class="primary" id="continue-as" style="width:100%;margin-bottom:0.75rem;">Continue as ${escapeHtml(last.name)}</button>` : ''}
    <p class="hint">${last ? 'Not you? Pick your name' : "Who's scanning?"}</p>
    ${operators.length > SEARCH_FROM ? '<input type="search" id="operator-search" placeholder="Type your name…" autocomplete="off" style="width:100%;margin-bottom:0.5rem;" />' : ''}
    <div class="operator-grid" id="operator-list"></div>
  `;
  const list = qs<HTMLDivElement>(body, '#operator-list');

  const pick = (op: OperatorSummary): void => renderPinEntry(body, operators, op);
  if (last) qs<HTMLButtonElement>(body, '#continue-as').addEventListener('click', () => pick(last));

  const draw = (query: string): void => {
    list.innerHTML = '';
    const shown = filterOperators(operators, query);
    if (shown.length === 0) {
      list.innerHTML = '<p class="hint">No one matches that name.</p>';
      return;
    }
    for (const op of shown) {
      const btn = document.createElement('button');
      btn.className = 'ghost';
      btn.textContent = op.name;
      btn.addEventListener('click', () => pick(op));
      list.appendChild(btn);
    }
  };
  draw('');
  const search = body.querySelector<HTMLInputElement>('#operator-search');
  search?.addEventListener('input', () => draw(search.value));
}

function renderPinEntry(body: HTMLElement, operators: OperatorSummary[], operator: OperatorSummary): void {
  body.innerHTML = `
    <div class="card center">
      <p class="hint">Logging in as</p>
      <p style="font-size:1.3rem;font-weight:700;margin:0.25rem 0 1rem;">${escapeHtml(operator.name)}</p>
      <label for="pin">PIN</label>
      <input type="password" id="pin" inputmode="numeric" pattern="[0-9]*" autocomplete="off" maxlength="8" placeholder="••••" style="text-align:center;letter-spacing:0.4em;font-size:1.6rem;" autofocus />
      <p id="status" class="hint error-text" style="min-height:1.2em;"></p>
      <div class="actions-row">
        <button class="ghost" id="change">Not you?</button>
        <button class="primary" id="submit">Log In</button>
      </div>
    </div>
  `;

  const pinInput = qs<HTMLInputElement>(body, '#pin');
  const status = qs<HTMLParagraphElement>(body, '#status');
  const submitBtn = qs<HTMLButtonElement>(body, '#submit');

  qs<HTMLButtonElement>(body, '#change').addEventListener('click', () => renderPicker(body, operators));

  async function submit(): Promise<void> {
    const pin = pinInput.value.trim();
    if (!/^\d{4,8}$/.test(pin)) {
      status.textContent = 'Enter your 4-8 digit PIN.';
      return;
    }
    status.textContent = '';
    submitBtn.disabled = true;
    submitBtn.textContent = 'Logging in…';
    try {
      const result = await api.login({ operatorId: operator.id, pin, deviceId: deviceId() });
      saveOperatorSession({ token: result.token, id: result.user.id, name: result.user.name, role: result.user.role });
      rememberOperator(result.user.id);
      location.hash = '#/';
    } catch (err) {
      pinInput.value = '';
      pinInput.focus();
      status.textContent = describeError(err);
      submitBtn.disabled = false;
      submitBtn.textContent = 'Log In';
    }
  }

  qs<HTMLButtonElement>(body, '#submit').addEventListener('click', () => void submit());
  pinInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') void submit();
  });
  pinInput.focus();
}

/** A stable per-device id, so Mintsoft/audit logs and the operator_sessions table can tell "same device logging back in" apart from "a different device". Not an identity credential by itself — just a label. */
function deviceId(): string {
  const key = 'wr.deviceId';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

function describeError(err: unknown): string {
  if (err instanceof ApiNetworkError) return 'Could not reach the server — check your connection or Settings.';
  if (err instanceof ApiError) {
    if (err.status === 401) return 'Incorrect PIN. Try again.';
    return `${err.message} (${err.status})`;
  }
  return String(err);
}
